// Console Glass palette — the single source of the console's colour tokens (task 355.3).
//
// Everything visual derives from here:
//   - AntD tokens (glassTheme.ts) read the values directly;
//   - CSS variables `--cg-*` are generated from these objects (paletteCss) and injected once by ThemeSync,
//     so CSS Modules and glass.css style through `var(--cg-…)` and never carry a literal colour.
//
// Night glass = dark, Day frost = light. Brand: corporate teal #00aaaa, slate #2c394c.
// Semantic colours (mint / amber / tangerine / rose / sky / slate) are reserved for status — never for decoration.

export interface Palette {
  /** Page ground: layered gradients painted on <body>. */
  ground: string;
  arc: string;
  arcGlow: string;
  blur: string;
  glass: string;
  glassRaised: string;
  solid: string;
  edge: string;
  edgeHi: string;
  /** Visible hairline on glass (borders that must survive the light ground). */
  rule: string;
  line: string;
  grid: string;
  ink: string;
  ink2: string;
  ink3: string;
  teal: string;
  tealSoft: string;
  mint: string;
  amber: string;
  /** Task 514.1 (owner Q5): Suspended — an account or customer held by us; the App's tangerine. */
  tangerine: string;
  rose: string;
  sky: string;
  violet: string;
  slate: string;
  /** Chart series, validated for CVD separation against this palette's ground. */
  series1: string;
  series2: string;
  /**
   * Workflow canvas arrows by who fires the transition (track 476.5). One categorical set per theme, validated
   * (dataviz checks: lightness band, chroma, colour-blind + normal-vision separation, 3:1 on `solid`). Teal is
   * the selection colour, so none of them uses it.
   */
  initOperator: string;
  initApp: string;
  initIntegration: string;
  initTimer: string;
  initSystem: string;
  shadow: string;
  cardShadow: string;
  /** 1 = neon glows on, 0 = off (Day frost). Used as a multiplier in calc(). */
  glow: string;
  /** Tint strength of status pills. */
  pillAlpha: string;
  field: string;
  fieldEdge: string;
  nav: string;
  navEdge: string;
  navInk: string;
  navInk2: string;
  navInkHover: string;
  navSelected: string;
  header: string;
  headerEdge: string;
  scroll: string;
}

export const NIGHT_PALETTE: Palette = {
  ground:
    'radial-gradient(1100px 620px at 8% -8%, rgba(0, 170, 170, 0.28), transparent 60%), ' +
    'radial-gradient(900px 720px at 100% 100%, rgba(44, 57, 76, 0.85), transparent 65%), ' +
    'radial-gradient(640px 460px at 70% 38%, rgba(0, 120, 140, 0.12), transparent 70%), ' +
    'linear-gradient(160deg, #08121a 0%, #060b11 55%, #0a121b 100%)',
  // Kept faint on purpose (task 357): at 0.5 the arc read as a line across the content.
  arc: 'rgba(70, 240, 228, 0.13)',
  arcGlow: '0 0 40px rgba(0, 210, 200, 0.07), inset 0 0 60px rgba(0, 210, 200, 0.03)',
  blur: 'blur(16px) saturate(140%)',
  glass: 'rgba(17, 32, 43, 0.5)',
  glassRaised: 'rgba(24, 44, 58, 0.62)',
  solid: '#0f1c25',
  edge: 'rgba(140, 235, 228, 0.12)',
  edgeHi: 'rgba(255, 255, 255, 0.07)',
  rule: 'rgba(140, 235, 228, 0.18)',
  line: 'rgba(255, 255, 255, 0.07)',
  grid: 'rgba(255, 255, 255, 0.06)',
  ink: '#e8f4f4',
  ink2: '#a0b6bd',
  ink3: '#6f868e',
  teal: '#22d3c8',
  tealSoft: 'rgba(34, 211, 200, 0.14)',
  mint: '#4be3a5',
  amber: '#f7b955',
  tangerine: '#ff9a4d',
  rose: '#ff6680',
  sky: '#62aeff',
  violet: '#a193ff',
  slate: '#8da0ac',
  series1: '#14a89f',
  series2: '#8a78ee',
  initOperator: '#4a96e5',
  initApp: '#00ae74',
  initIntegration: '#8f80ea',
  initTimer: '#c0850c',
  initSystem: '#cd68b7',
  shadow: '0 30px 60px -30px rgba(0, 0, 0, 0.75)',
  cardShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 24px 48px -24px rgba(0, 0, 0, 0.7)',
  glow: '1',
  pillAlpha: '13%',
  field: 'rgba(255, 255, 255, 0.04)',
  fieldEdge: 'rgba(140, 235, 228, 0.16)',
  nav: 'rgba(8, 18, 26, 0.55)',
  navEdge: 'rgba(130, 230, 225, 0.09)',
  navInk: 'rgba(214, 234, 236, 0.72)',
  navInk2: 'rgba(214, 234, 236, 0.4)',
  navInkHover: '#e5f1f2',
  navSelected: '#7df4ea',
  header: 'rgba(8, 18, 26, 0.45)',
  headerEdge: 'rgba(130, 230, 225, 0.08)',
  scroll: 'rgba(140, 235, 228, 0.2)',
};

export const DAY_PALETTE: Palette = {
  ground:
    'radial-gradient(1100px 620px at 8% -10%, rgba(0, 170, 170, 0.2), transparent 62%), ' +
    'radial-gradient(900px 700px at 105% 105%, rgba(44, 57, 76, 0.16), transparent 65%), ' +
    'linear-gradient(170deg, #eef4f5, #e3ebee)',
  arc: 'rgba(0, 170, 170, 0.12)',
  arcGlow: '0 0 30px rgba(0, 170, 170, 0.05), inset 0 0 40px rgba(0, 170, 170, 0.02)',
  blur: 'blur(14px) saturate(150%)',
  glass: 'rgba(255, 255, 255, 0.62)',
  glassRaised: 'rgba(255, 255, 255, 0.82)',
  solid: '#fbfdfd',
  edge: 'rgba(255, 255, 255, 0.95)',
  edgeHi: '#ffffff',
  rule: 'rgba(16, 28, 37, 0.14)',
  line: 'rgba(16, 28, 37, 0.08)',
  grid: 'rgba(16, 28, 37, 0.07)',
  ink: '#101c25',
  ink2: '#50626c',
  ink3: '#83939b',
  teal: '#008f8f',
  tealSoft: 'rgba(0, 143, 143, 0.1)',
  mint: '#14814f',
  amber: '#a86400',
  tangerine: '#c2560f',
  rose: '#c92c47',
  sky: '#1e69c9',
  violet: '#5a47d1',
  slate: '#5f707a',
  series1: '#008f8f',
  series2: '#5a47d1',
  initOperator: '#1e69c9',
  initApp: '#14814f',
  initIntegration: '#5a47d1',
  initTimer: '#a86400',
  initSystem: '#b0359a',
  shadow: '0 18px 40px -20px rgba(22, 52, 72, 0.28)',
  cardShadow: 'inset 0 1px 0 #ffffff, 0 14px 34px -16px rgba(22, 52, 72, 0.22)',
  glow: '0',
  pillAlpha: '10%',
  field: 'rgba(255, 255, 255, 0.75)',
  fieldEdge: 'rgba(16, 28, 37, 0.12)',
  nav: 'rgba(20, 32, 44, 0.94)',
  navEdge: 'rgba(255, 255, 255, 0.06)',
  navInk: 'rgba(222, 236, 238, 0.74)',
  navInk2: 'rgba(222, 236, 238, 0.42)',
  navInkHover: '#ffffff',
  navSelected: '#86f6ec',
  header: 'rgba(255, 255, 255, 0.55)',
  headerEdge: 'rgba(19, 32, 41, 0.07)',
  scroll: 'rgba(19, 32, 41, 0.22)',
};

export const FONT_DISPLAY = "'Sora Variable', 'IBM Plex Sans Variable', system-ui, sans-serif";
export const FONT_SANS =
  "'IBM Plex Sans Variable', 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
export const FONT_MONO =
  "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";

/** `series1` → `--cg-series-1`, `navInk2` → `--cg-nav-ink-2`. */
export function cssVarName(key: string): string {
  return `--cg-${key.replace(/([a-z])([A-Z0-9])/g, '$1-$2').toLowerCase()}`;
}

function declarations(palette: Palette): string {
  return Object.entries(palette)
    .map(([key, value]) => `  ${cssVarName(key)}: ${value};`)
    .join('\n');
}

/**
 * The stylesheet ThemeSync injects: Day frost on :root (default and data-theme="light"),
 * Night glass on data-theme="dark", plus the three font stacks.
 */
export function paletteCss(): string {
  return [
    `:root, :root[data-theme='light'] {\n${declarations(DAY_PALETTE)}\n}`,
    `:root[data-theme='dark'] {\n${declarations(NIGHT_PALETTE)}\n}`,
    `:root {\n  --cg-font-display: ${FONT_DISPLAY};\n  --cg-font-sans: ${FONT_SANS};\n  --cg-font-mono: ${FONT_MONO};\n}`,
  ].join('\n');
}

/** A palette hex colour at the given opacity, for places that need a real colour value (SVG attributes). */
export function tint(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
